import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Inbox } from "lucide-react";

export const Logo = ({ dark, className = "" }) => (
  <span className={`inline-flex items-center ${dark ? "bg-[#FDFCF8] rounded-md px-2 py-1" : ""} ${className}`} data-testid="brand-logo">
    <img src="/twothread-logo.png" alt="TwoThread Accord" className="h-10 w-auto" />
  </span>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Card = ({ children, className = "", ...p }) => (
  <div className={`bg-white border border-slate-200/80 rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.03)] ${className}`} {...p}>{children}</div>
);

export const Stat = ({ label, value, hint, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
    <div className="text-2xl font-display font-bold mt-2 text-slate-900">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </Card>
);

export const Field = ({ label, hint, textarea, testid, ...p }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-xs font-semibold text-slate-600">{label}</Label>}
    {textarea ? <Textarea data-testid={testid} {...p} /> : <Input data-testid={testid} {...p} />}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const TONE = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
  blue: "bg-indigo-50 text-indigo-700 border-indigo-200",
  gray: "bg-slate-100 text-slate-600 border-slate-200",
};
const MAP = {
  active: "green", paid: "green", published: "green", delivered: "green", connected: "green", completed: "green", verified: "green", sent: "green", fulfilled: "green", cod_collected: "green", closed: "gray",
  trialing: "blue", confirmed: "blue", processing: "blue", packed: "blue", shipped: "blue", approved: "blue", in_transit: "blue", received: "blue", inspected: "blue", preview: "blue",
  pending: "amber", pending_payment: "amber", cod_pending: "amber", draft: "amber", requested: "amber", unfulfilled: "amber", return_requested: "amber", manual_pending: "amber", invited: "amber", past_due: "amber", partially_refunded: "amber",
  failed: "red", payment_failed: "red", cancelled: "red", suspended: "red", expired: "red", error: "red", rejected: "red", refunded: "gray", archived: "gray",
};
export const Status = ({ value, testid }) => (
  <Badge variant="outline" className={`capitalize font-medium ${TONE[MAP[value] || "gray"]}`} data-testid={testid}>
    {String(value || "—").replace(/_/g, " ")}
  </Badge>
);

export const Empty = ({ title, body, children }) => (
  <div className="text-center py-16 px-6" data-testid="empty-state">
    <Inbox className="mx-auto h-8 w-8 text-slate-300" />
    <div className="mt-3 font-semibold text-slate-800">{title}</div>
    {body && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{body}</p>}
    <div className="mt-4 flex justify-center gap-2">{children}</div>
  </div>
);

export const Spinner = () => (
  <div className="py-20 grid place-items-center" data-testid="loading-spinner"><Loader2 className="animate-spin h-6 w-6 text-indigo-500" /></div>
);

export const Table = ({ head, children, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-slate-50/80 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr>{head.map((h, i) => <th key={i} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
    </table>
  </div>
);

export const Td = ({ children, className = "", ...rest }) => <td className={`px-4 py-3 ${className}`} {...rest}>{children}</td>;

export const Select = ({ value, onChange, options, testid, className = "" }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${className}`}>
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);
